import type {
    BooleanScaleData,
    CycleScaleData,
    MealScaleData,
    MoodScaleData,
    OvulationScaleData,
    ScaleData,
    SexScaleData,
    SleepScaleData,
    WaterScaleData,
} from "@qendo/profile/journal";
import {
    type Cycle,
    CycleMap,
    MealTypeMap,
    MoodMap,
    OvulationMap,
    ScaleType,
    type SexType,
    SexTypeMap,
} from "@qendo/trackables";
import type { DateArg } from "date-fns";
import { View } from "react-native";
import { DataListItem } from "~/components/layout/DataListItem";
import { JournalLine } from "~/components/layout/journal/JournalLine";
import { BodyText } from "~/components/layout/text";
import { Nothing } from "~/components/permission/Nothing";
import { Only } from "~/components/permission/Only";
import { QendoIcon } from "~/components/QendoIcon";
import { Flex, Gap, Items } from "~/constants/layout";
import { formatTimeForHumans, parseTime } from "~/util/dates";

export type ScaleDisplayProps<S extends ScaleType, D extends ScaleData<S>> = {
    readonly scale: S;
    readonly label?: string;
    value: D;
    asDataList?: boolean;
};

export const ScaleOutput = <S extends ScaleType = ScaleType, D extends ScaleData<S> = ScaleData<S>>({
    scale,
    label,
    value,
    asDataList = false,
}: ScaleDisplayProps<S, D>) => {
    const Renderer = asDataList ? DataListItem : JournalLine;

    switch (scale) {
        case ScaleType.boolean: {
            const checked = (value as BooleanScaleData).value;
            return <Renderer title={label} data={checked ? "Yes" : "No"} />;
        }
        case ScaleType.cycle: {
            const cycle = (value as CycleScaleData).value;
            return <Renderer title="Period" data={CycleMap[cycle as Cycle] ?? null} />;
        }
        case ScaleType.meal: {
            const meal = (value as MealScaleData).value;
            return <Renderer title="Meal" data={meal ? MealTypeMap[meal] : null} />;
        }
        case ScaleType.mood: {
            const { value: mood, feelings } = value as MoodScaleData;

            return (
                <>
                    <Renderer title="Mood" data={mood ? MoodMap[mood] : null} />
                    <Only when={feelings.length}>
                        <Renderer title="Feeling" data={feelings.join(", ")} />
                    </Only>
                </>
            );
        }
        case ScaleType.ovulation: {
            const { value: intensity, mucus, basal, breast, headache } = value as OvulationScaleData;

            const additional_symptoms = Object.entries({
                [OvulationMap.mucus]: mucus,
                [OvulationMap.basal]: basal,
                [OvulationMap.breast]: breast,
                [OvulationMap.headache]: headache,
            })
                .filter(([, set]) => set)
                .map(([value], idx) => [value, idx])
                .map(([value, idx]) =>
                    asDataList ? (
                        <View style={[Flex.row, Gap.xs, Items.start]} key={`symptom_${idx}`}>
                            <QendoIcon name="circle-sm-filled" />
                            <BodyText>{value}</BodyText>
                        </View>
                    ) : (
                        value
                    ),
                );

            return (
                <>
                    <Only when={intensity}>
                        <Renderer title={label ?? "Intensity"} data={intensity} />
                    </Only>
                    <Only when={additional_symptoms}>
                        <Renderer
                            title="Other symptoms"
                            data={asDataList ? <View>{additional_symptoms}</View> : additional_symptoms.join("; ")}
                        />
                    </Only>
                </>
            );
        }
        case ScaleType.sex: {
            const sex = (value as SexScaleData).value;
            return (
                <Only not when={sex === null}>
                    <Renderer data={SexTypeMap[sex as SexType]} />
                </Only>
            );
        }
        case ScaleType.sleep: {
            const { value: quality, start, end } = value as SleepScaleData;

            return (
                <>
                    <Only when={start}>
                        <Renderer title="Started" data={formatTimeForHumans(parseTime(start as DateArg<Date>))} />
                    </Only>
                    <Only when={end}>
                        <Renderer title="Ended" data={formatTimeForHumans(parseTime(end as DateArg<Date>))} />
                    </Only>
                    <Only when={quality}>
                        <Renderer title="Quality" data={quality} />
                    </Only>
                </>
            );
        }
        case ScaleType.water: {
            const { value: amount, volume } = value as WaterScaleData;

            return (
                <Only when={amount}>
                    <Renderer title="Amount" data={`${amount} ${volume}`} />
                </Only>
            );
        }
        case ScaleType.none:
            return <Nothing />;
        default:
            return <Renderer title={label ?? "Intensity"} data={value.value as string | number | null | undefined} />;
    }
};
